import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ReactComponent as Copy } from '../../../assets/icons/copy.svg';
// import { ReactComponent as ShieldCheck } from '../../../assets/icons/security-pass.svg';
import { ReactComponent as Settings } from '../../../assets/icons/setting-icon.svg';
import config from '../../../config/config';
import { useAppDispatch } from '../../../redux/hooks';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import MyButton from '../../../shared-components/button/Button';

/**
 * ConnectionSidePane properties interface
 */
interface ConnectionSidePaneProperties {
  connection: any;
  activeRole: string;
  // onAuthorize?: () => void;
}

/**
 * Renders a labeled, copyable token/URL value row.
 * @param {object} properties - Component properties.
 * @param {string} properties.label - The label describing the value.
 * @param {string} properties.value - The value to display and copy.
 * @param {Function} properties.onCopy - Callback invoked with the value when the copy button is clicked.
 * @returns {any} The rendered component.
 */
function TokenRow({
  label,
  value,
  onCopy,
}: Readonly<{ label: string; value: string; onCopy: (value: string, label: string) => void }>) {
  const { t } = useTranslation('ocpi');

  return (
    <div className="token-item">
      <span className="token-label">{label}</span>
      <div className="token-value-box">
        <code>{value}</code>
        <button
          type="button"
          className="copy-btn"
          title={t('copyItem', { label })}
          onClick={() => onCopy(value, label)}>
          <Copy height={14} width={14} />
        </button>
      </div>
    </div>
  );
}

/**
 * Shows the full OCPI connection details for a partner — authentication tokens and a link
 * through to the full connection management screen. Rendered as the content of a ModalBox
 * when a connection row is clicked in OCPIConnections.
 * @param {ConnectionSidePaneProperties} properties - Component properties.
 * @param {any} properties.connection - The connection to display.
 * @param {string} properties.activeRole - The current active role, used to resolve which side
 * (sender/receiver) of the connection is the partner.
 * @returns {any} The rendered component.
 */
function ConnectionSidePane({
  connection,
  activeRole,
  // onAuthorize,
}: ConnectionSidePaneProperties): JSX.Element | null {
  const { t } = useTranslation('ocpi');
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  if (!connection) return null;

  /**
   * Copies the given value to the clipboard and shows a success notification.
   * @param {string} value - The text to copy.
   * @param {string} label - A label describing the value, used in the success message.
   */
  const copyToClipboard = (value: string, label: string) => {
    if (!value) return;
    navigator.clipboard.writeText(value);
    dispatch(
      setNotificationMessage({
        message: t('copiedToClipboard', { label }),
        status: true,
        type: 'success',
      }),
    );
  };

  /**
   * Navigates to the full connection workspace for the active connection.
   */
  const handleManageConnection = () => {
    navigate(`/ev-admin/ocpi/connections/${connection._id || connection.id}`);
  };

  const isWeSender = connection.senderRole?.toUpperCase() === activeRole?.toUpperCase();
  const partnerPartyId = isWeSender ? connection.partyId : connection.senderPartyId;
  const partnerCountryCode = isWeSender ? connection.countryCode : connection.senderCountryCode;
  const partnerRole = isWeSender ? connection.role : connection.senderRole;
  const defaultVersionsUrl = `${config.ocpiApiUrl.replace(/\/$/, '')}/versions`;
  const isPending = (connection.status || 'PENDING') === 'PENDING';

  return (
    <div className="ocpi-connection-side-panel">
      <div className="connection-meta">
        <div className="title-row">
          <h2>{connection.businessDetails?.name || t('partnerWithId', { id: partnerPartyId })}</h2>
          <span className={`status-badge ${(connection.status || 'PENDING').toLowerCase()}`}>
            {connection.status === 'CONNECTED' ? t('connected') : t('pending')}
          </span>
          {/* {isPending && onAuthorize && (
            <button
              type="button"
              className="authorize-btn"
              title={t('ocpi:authorize')}
              onClick={onAuthorize}>
              <ShieldCheck height={16} width={16} />
              <span>{t('ocpi:authorize')}</span>
            </button>
          )} */}
        </div>
        <p className="subtitle">
          {partnerCountryCode}-{partnerPartyId} • {partnerRole}
        </p>
      </div>

      <div className="pane-body">
        <div className="section">
          <h4 className="section-title">{t('authenticationTokens')}</h4>
          {isPending ? (
            <>
              <TokenRow
                label={t('versionsUrl')}
                value={connection.versionsUrl || defaultVersionsUrl}
                onCopy={copyToClipboard}
              />
              {connection.tokenA && (
                <TokenRow
                  label={t('tokenARegistration')}
                  value={connection.tokenA}
                  onCopy={copyToClipboard}
                />
              )}
            </>
          ) : (
            <>
              <TokenRow
                label={t('tokenCReceiver')}
                value={connection.tokenC || '********************'}
                onCopy={copyToClipboard}
              />
              <TokenRow
                label={t('tokenBSender')}
                value={connection.tokenB || '********************'}
                onCopy={copyToClipboard}
              />
            </>
          )}
        </div>

        <div className="section">
          <h4 className="section-title">{t('connectionSpecificData')}</h4>
          <p className="section-desc">
            {t('connectionSpecificDataDescription')}
          </p>
          <div className="manage-connection-action">
            <MyButton
              label={t('manageFullConnection')}
              buttonType="primary"
              svgIcon={<Settings height={14} width={14} />}
              onClickFunc={handleManageConnection}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

ConnectionSidePane.defaultProps = {
  // onAuthorize: undefined,
};

export default ConnectionSidePane;
